// @ts-nocheck
import { useState, useMemo, memo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApiMutation } from "@/hooks/useApiMutation";
import { useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Plus, Pencil, Trash2, Package, Wrench, Gift, FolderPlus, Tags, Copy, Upload, ChefHat, Hammer, Download } from "lucide-react";
import { type StarterIngredient, type StarterMaterial, type StarterIngredientCategory, type StarterMaterialCategory } from "@/lib/types";
import { formatCurrency } from "@/lib/currency";
import { useAuth } from "@/lib/auth-context";
import { useStarterIngredients } from "@/hooks/useStarterIngredients";
import { useStarterMaterials } from "@/hooks/useStarterMaterials";
import { useStarterEquipment } from "@/hooks/useStarterEquipment";
import { useStarterCategories } from "@/hooks/useStarterCategories";
import { StarterIngredientCategoryCombobox } from "@/components/StarterIngredientCategoryCombobox";
import { StarterMaterialCategoryCombobox } from "@/components/StarterMaterialCategoryCombobox";
import { StarterEquipmentCategoryCombobox } from "@/components/StarterEquipmentCategoryCombobox";
import { StarterIngredientsTab } from "@/components/starter-pack/StarterIngredientsTab";
import { StarterMaterialsTab } from "@/components/starter-pack/StarterMaterialsTab";
import { StarterEquipmentTab } from "@/components/starter-pack/StarterEquipmentTab";
import { StarterCategoriesTab } from "@/components/starter-pack/StarterCategoriesTab";
import { Pagination } from "@/components/Pagination";
import * as XLSX from 'xlsx';
import { showSuccess, showError } from "@/lib/toastHelpers";

const DEFAULT_INGREDIENT_CATEGORIES = [
  "Dairy", "Eggs", "Flour & Grains", "Sugar & Sweeteners", "Fats & Oils",
  "Leavening", "Chocolate & Cocoa", "Nuts & Seeds", "Fruits", "Spices & Flavorings",
  "Milk & Cream", "Other"
];

const DEFAULT_MATERIAL_CATEGORIES = [
  "Packaging", "Containers", "Labels", "Equipment", "Utensils", "Other"
];

const UNITS = ["pcs", "pack", "box", "roll", "g", "kg", "ml", "l", "cm", "m", "ft", "in"];

const StarterPackContent = memo(function StarterPackContent() {
  const [location] = useLocation();

  // Don't run hooks if we're on a sub-page
  const isOnSubPage = location.includes('/add') || location.includes('/edit');

  if (isOnSubPage) {
    return null; // Don't render anything on sub-pages
  }
  const { user } = useAuth();
  const currency = user?.currency || "PHP";

  // In admin-app, always treat as admin
  const isAdminWorkspace = true;
  const [, setLocation] = useLocation();
  const [activeTab, setActiveTab] = useState("ingredients");
  const [materialSearchQuery, setMaterialSearchQuery] = useState("");
  const [equipmentSearchQuery, setEquipmentSearchQuery] = useState("");

  // Ingredient form state
  const [ingredientDialogOpen, setIngredientDialogOpen] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState<StarterIngredient | null>(null);
  const [ingredientPage, setIngredientPage] = useState(1);
  const [materialPage, setMaterialPage] = useState(1);
  const [equipmentPage, setEquipmentPage] = useState(1);
  const [ingredientSortField, setIngredientSortField] = useState<string>("name");
  const [ingredientSortDirection, setIngredientSortDirection] = useState<"asc" | "desc">("asc");
  const [materialSortField, setMaterialSortField] = useState<string>("name");
  const [materialSortDirection, setMaterialSortDirection] = useState<"asc" | "desc">("asc");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [ingredientForm, setIngredientForm] = useState({
    name: "",
    categoryId: "",
    pricePerGram: "",
    quantity: "0",
    unit: "g",
    purchaseAmount: "",
    isCountBased: false,
    purchaseUnit: "",
    piecesPerPurchaseUnit: "",
    weightPerPiece: "",
  });

  const resetIngredientForm = () => {
    setIngredientForm({
      name: "",
      categoryId: "",
      pricePerGram: "",
      quantity: "0",
      unit: "g",
      purchaseAmount: "",
      isCountBased: false,
      purchaseUnit: "",
      piecesPerPurchaseUnit: "",
      weightPerPiece: "",
    });
    setAddingIngredientCategory(false);
    setNewIngredientCategory("");
  };

  // Use custom hooks
  const {
    starterIngredients,
    ingredientsLoading,
    createIngredientMutation,
    updateIngredientMutation,
    deleteIngredientMutation,
    importIngredientMutation,
    importAllIngredientsMutation,
  } = useStarterIngredients({
    onCreateSuccess: () => {
      setIngredientDialogOpen(false);
      resetIngredientForm();
    },
    onUpdateSuccess: () => {
      setIngredientDialogOpen(false);
      setEditingIngredient(null);
      resetIngredientForm();
    },
  });

  // Custom categories (user-added)
  const [customIngredientCategories, setCustomIngredientCategories] = useState<string[]>([]);
  const [customMaterialCategories, setCustomMaterialCategories] = useState<string[]>([]);

  // File upload state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMaterialDialogOpen, setUploadMaterialDialogOpen] = useState(false);
  const [uploadingMaterial, setUploadingMaterial] = useState(false);
  const [equipmentUploadDialogOpen, setEquipmentUploadDialogOpen] = useState(false);

  // Add category mode state
  const [addingIngredientCategory, setAddingIngredientCategory] = useState(false);
  const [newIngredientCategory, setNewIngredientCategory] = useState("");
  const [addingMaterialCategory, setAddingMaterialCategory] = useState(false);
  const [newMaterialCategory, setNewMaterialCategory] = useState("");

  // Material form state
  const [materialDialogOpen, setMaterialDialogOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<StarterMaterial | null>(null);
  const [materialForm, setMaterialForm] = useState({
    name: "",
    categoryId: "",
    quantity: "",
    unit: "pcs",
    pricePerUnit: "",
    purchaseAmount: "",
    notes: "",
  });

  const {
    starterMaterials,
    isLoading: materialsLoading,
    createMaterialMutation,
    updateMaterialMutation,
    deleteMaterialMutation,
  } = useStarterMaterials({
    onCreateSuccess: () => {
      setMaterialDialogOpen(false);
      resetMaterialForm();
    },
    onUpdateSuccess: () => {
      setMaterialDialogOpen(false);
      setEditingMaterial(null);
      resetMaterialForm();
    },
  });

  // Equipment query
  const {
    starterEquipment,
    isLoading: equipmentLoading,
    createEquipmentMutation,
    updateEquipmentMutation,
    deleteEquipmentMutation,
  } = useStarterEquipment();

  // Category hooks
  const {
    categories: starterIngredientCategories,
    isLoading: ingredientCategoriesLoading,
    createMutation: createIngredientCategoryMutation,
    updateMutation: updateIngredientCategoryMutation,
    deleteMutation: deleteIngredientCategoryMutation,
  } = useStarterCategories('ingredients');

  const {
    categories: starterMaterialCategories,
    isLoading: materialCategoriesLoading,
    createMutation: createMaterialCategoryMutation,
    updateMutation: updateMaterialCategoryMutation,
    deleteMutation: deleteMaterialCategoryMutation,
  } = useStarterCategories('materials');

  const {
    categories: starterEquipmentCategories,
    isLoading: equipmentCategoriesLoading,
    createMutation: createEquipmentCategoryMutation,
    updateMutation: updateEquipmentCategoryMutation,
    deleteMutation: deleteEquipmentCategoryMutation,
  } = useStarterCategories('equipment');

  const {
    categories: starterRecipeCategories,
    isLoading: recipeCategoriesLoading,
    createMutation: createRecipeCategoryMutation,
    updateMutation: updateRecipeCategoryMutation,
    deleteMutation: deleteRecipeCategoryMutation,
  } = useStarterCategories('recipes');

  // Category state
  const [ingredientCategoryDialogOpen, setIngredientCategoryDialogOpen] = useState(false);
  const [materialCategoryDialogOpen, setMaterialCategoryDialogOpen] = useState(false);
  const [recipeCategoryDialogOpen, setRecipeCategoryDialogOpen] = useState(false);
  const [editingIngredientCategory, setEditingIngredientCategory] = useState<StarterIngredientCategory | null>(null);
  const [editingMaterialCategory, setEditingMaterialCategory] = useState<StarterMaterialCategory | null>(null);
  const [editingRecipeCategory, setEditingRecipeCategory] = useState<StarterMaterialCategory | null>(null);
  const [ingredientCategoryName, setIngredientCategoryName] = useState("");
  const [ingredientCategoryNotes, setIngredientCategoryNotes] = useState("");
  const [materialCategoryName, setMaterialCategoryName] = useState("");
  const [materialCategoryNotes, setMaterialCategoryNotes] = useState("");
  const [recipeCategoryName, setRecipeCategoryName] = useState("");
  const [recipeCategoryNotes, setRecipeCategoryNotes] = useState("");

  // Get categories from database or use defaults
  const allIngredientCategories = useMemo(() => {
    const dbCategories = starterIngredientCategories?.map(c => c.name) || [];
    const combined = dbCategories.length > 0
      ? dbCategories
      : DEFAULT_INGREDIENT_CATEGORIES;
    return Array.from(new Set(combined)).sort();
  }, [starterIngredientCategories]);

  const allMaterialCategories = useMemo(() => {
    const dbCategories = starterMaterialCategories?.map(c => c.name) || [];
    const combined = dbCategories.length > 0
      ? dbCategories
      : DEFAULT_MATERIAL_CATEGORIES;
    return Array.from(new Set(combined)).sort();
  }, [starterMaterialCategories]);

  // Sorting logic
  const sortedIngredients = useMemo(() => {
    if (!starterIngredients) return [];
    const sorted = [...starterIngredients].sort((a, b) => {
      let aVal = a[ingredientSortField as keyof typeof a];
      let bVal = b[ingredientSortField as keyof typeof b];

      // Handle category sorting by looking up name
      if (ingredientSortField === 'category') {
        aVal = starterIngredientCategories?.find(c => c._id === a.categoryId)?.name || a.category || '';
        bVal = starterIngredientCategories?.find(c => c._id === b.categoryId)?.name || b.category || '';
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return ingredientSortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      const aNum = parseFloat(aVal as string) || 0;
      const bNum = parseFloat(bVal as string) || 0;
      return ingredientSortDirection === 'asc' ? aNum - bNum : bNum - aNum;
    });
    return sorted;
  }, [starterIngredients, ingredientSortField, ingredientSortDirection, starterIngredientCategories]);

  const sortedMaterials = useMemo(() => {
    if (!starterMaterials) return [];

    // Filter by search query
    const filtered = starterMaterials.filter(mat =>
      mat.name.toLowerCase().includes(materialSearchQuery.toLowerCase()) ||
      (starterMaterialCategories?.find(c => c._id === mat.categoryId)?.name || mat.category || '').toLowerCase().includes(materialSearchQuery.toLowerCase())
    );

    const sorted = [...filtered].sort((a, b) => {
      let aVal = a[materialSortField as keyof typeof a];
      let bVal = b[materialSortField as keyof typeof b];

      // Handle category sorting by looking up name
      if (materialSortField === 'category') {
        aVal = starterMaterialCategories?.find(c => c._id === a.categoryId)?.name || a.category || '';
        bVal = starterMaterialCategories?.find(c => c._id === b.categoryId)?.name || b.category || '';
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return materialSortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      const aNum = parseFloat(aVal as string) || 0;
      const bNum = parseFloat(bVal as string) || 0;
      return materialSortDirection === 'asc' ? aNum - bNum : bNum - aNum;
    });
    return sorted;
  }, [starterMaterials, materialSortField, materialSortDirection, starterMaterialCategories, materialSearchQuery]);

  // Pagination logic
  const paginatedIngredients = useMemo(() => {
    if (!sortedIngredients) return [];
    const start = (ingredientPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return sortedIngredients.slice(start, end);
  }, [sortedIngredients, ingredientPage]);

  const paginatedMaterials = useMemo(() => {
    if (!sortedMaterials) return [];
    const start = (materialPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return sortedMaterials.slice(start, end);
  }, [sortedMaterials, materialPage]);

  const ingredientTotalPages = Math.max(1, Math.ceil((sortedIngredients?.length || 0) / itemsPerPage));
  const materialTotalPages = Math.max(1, Math.ceil((sortedMaterials?.length || 0) / itemsPerPage));


  // Import all materials
  const importAllMaterialsMutation = useApiMutation({
    endpoint: "/api/starter-pack/import/materials",
    method: "POST",
    invalidate: [["/api/materials"]],
    onSuccess: (data: any) => showSuccess(`Imported ${data.imported} materials to your pantry`),
    errorMessage: "Failed to import materials",
  });

  // Import single material
  const importMaterialMutation = useApiMutation({
    endpoint: "/api/starter-pack/import/materials",
    method: "POST",
    invalidate: [["/api/materials"]],
    successMessage: "Material copied to your pantry",
    errorMessage: "Failed to copy material",
  });



  // Wrapper functions for category mutations with UI state management
  const handleCreateIngredientCategory = (data: { name: string; notes: string }) => {
    createIngredientCategoryMutation.mutate(data, {
      onSuccess: () => {
        setIngredientCategoryDialogOpen(false);
        setIngredientCategoryName("");
        setIngredientCategoryNotes("");
      }
    });
  };

  const handleUpdateIngredientCategory = (id: string, data: { name: string; notes: string }) => {
    updateIngredientCategoryMutation.mutate({ id, data }, {
      onSuccess: () => {
        setIngredientCategoryDialogOpen(false);
        setEditingIngredientCategory(null);
        setIngredientCategoryName("");
        setIngredientCategoryNotes("");
      }
    });
  };

  const handleCreateMaterialCategory = (data: { name: string; notes: string }) => {
    createMaterialCategoryMutation.mutate(data, {
      onSuccess: () => {
        setMaterialCategoryDialogOpen(false);
        setMaterialCategoryName("");
        setMaterialCategoryNotes("");
      }
    });
  };

  const handleUpdateMaterialCategory = (id: string, data: { name: string; notes: string }) => {
    updateMaterialCategoryMutation.mutate({ id, data }, {
      onSuccess: () => {
        setMaterialCategoryDialogOpen(false);
        setEditingMaterialCategory(null);
        setMaterialCategoryName("");
        setMaterialCategoryNotes("");
      }
    });
  };

  const handleCreateRecipeCategory = (data: { name: string; notes: string }) => {
    createRecipeCategoryMutation.mutate(data, {
      onSuccess: () => {
        setRecipeCategoryDialogOpen(false);
        setRecipeCategoryName("");
        setRecipeCategoryNotes("");
      }
    });
  };

  const handleUpdateRecipeCategory = (id: string, data: { name: string; notes: string }) => {
    updateRecipeCategoryMutation.mutate({ id, data }, {
      onSuccess: () => {
        setRecipeCategoryDialogOpen(false);
        setEditingRecipeCategory(null);
        setRecipeCategoryName("");
        setRecipeCategoryNotes("");
      }
    });
  };

  const resetMaterialForm = () => {
    setMaterialForm({
      name: "",
      categoryId: "",
      quantity: "",
      unit: "pcs",
      pricePerUnit: "",
      purchaseAmount: "",
      notes: "",
    });
    setAddingMaterialCategory(false);
    setNewMaterialCategory("");
  };

  const handleAddIngredientCategory = () => {
    const trimmed = newIngredientCategory.trim();
    if (trimmed && !allIngredientCategories.includes(trimmed)) {
      setCustomIngredientCategories(prev => [...prev, trimmed]);
      setIngredientForm({ ...ingredientForm, category: trimmed });
      showSuccess(`"${trimmed}" added to ingredient categories`, "Category added");
    } else if (trimmed) {
      setIngredientForm({ ...ingredientForm, category: trimmed });
    }
    setAddingIngredientCategory(false);
    setNewIngredientCategory("");
  };

  const handleAddMaterialCategory = () => {
    const trimmed = newMaterialCategory.trim();
    if (trimmed && !allMaterialCategories.includes(trimmed)) {
      setCustomMaterialCategories(prev => [...prev, trimmed]);
      setMaterialForm({ ...materialForm, category: trimmed });
      showSuccess(`"${trimmed}" added to material categories`, "Category added");
    } else if (trimmed) {
      setMaterialForm({ ...materialForm, category: trimmed });
    }
    setAddingMaterialCategory(false);
    setNewMaterialCategory("");
  };

  const openEditIngredient = (item: StarterIngredient) => {
    setLocation(`/starter-pack/ingredient?edit=${item._id}`);
  };

  const openEditMaterial = (item: StarterMaterial) => {
    setEditingMaterial(item);
    setMaterialForm({
      name: item.name,
      categoryId: item.categoryId || "",
      quantity: item.quantity || "",
      unit: item.unit || "pcs",
      pricePerUnit: item.pricePerUnit || "",
      purchaseAmount: item.purchaseAmount || "",
      notes: item.notes || "",
    });
    setMaterialDialogOpen(true);
  };

  const handleIngredientSubmit = () => {
    // pricePerGram is intentionally NOT computed here — see AddStarterIngredient.tsx for why
    // (no unit-conversion utility in this app; the backend derives it via
    // PantryPricingService, the same source of truth every real ingredient route uses).
    // Bug #81. (Dead code today — nothing wires up to this dialog/handler — fixed anyway so
    // reviving it doesn't reintroduce the bug.)
    const data = {
      name: ingredientForm.name,
      categoryId: ingredientForm.categoryId || null,
      quantity: ingredientForm.quantity || "0",
      unit: ingredientForm.unit || null,
      purchaseAmount: ingredientForm.purchaseAmount || null,
      isCountBased: ingredientForm.isCountBased,
      purchaseUnit: ingredientForm.isCountBased ? ingredientForm.purchaseUnit : null,
      piecesPerPurchaseUnit: ingredientForm.isCountBased ? ingredientForm.piecesPerPurchaseUnit : null,
      weightPerPiece: ingredientForm.isCountBased ? ingredientForm.weightPerPiece : null,
    };

    if (editingIngredient) {
      updateIngredientMutation.mutate({ id: editingIngredient._id, ...data });
    } else {
      createIngredientMutation.mutate(data);
    }
  };

  const handleMaterialSubmit = () => {
    // Auto-calculate pricePerUnit if quantity and purchaseAmount are provided
    let calculatedPricePerUnit = materialForm.pricePerUnit;
    if (materialForm.quantity && materialForm.purchaseAmount) {
      const qty = parseFloat(materialForm.quantity);
      const purchase = parseFloat(materialForm.purchaseAmount);
      if (qty > 0 && purchase > 0) {
        calculatedPricePerUnit = (purchase / qty).toFixed(4);
      }
    }

    const data = {
      name: materialForm.name,
      categoryId: materialForm.categoryId || null,
      quantity: materialForm.quantity || null,
      unit: materialForm.unit || null,
      pricePerUnit: calculatedPricePerUnit || null,
      purchaseAmount: materialForm.purchaseAmount || null,
      notes: materialForm.notes || null,
    };

    if (editingMaterial) {
      updateMaterialMutation.mutate({ id: editingMaterial._id, ...data });
    } else {
      createMaterialMutation.mutate(data);
    }
  };

  const downloadStarterPackCSV = () => {
    const wb = XLSX.utils.book_new();
    const catName = (cats: any[] | undefined, id: string) => cats?.find(c => c._id === id)?.name || '';

    const ingRows = (starterIngredients || []).map(i => ({
      Name: i.name, Category: catName(starterIngredientCategories, i.categoryId), Quantity: i.quantity || '', Unit: i.unit || '',
      'Purchase Amount': i.purchaseAmount || '', 'Price Per Gram': i.pricePerGram || '',
      'Count Based': i.isCountBased ? 'Yes' : 'No', 'Purchase Unit': i.purchaseUnit || '',
      'Pieces Per Unit': i.piecesPerPurchaseUnit || '', 'Weight Per Piece': i.weightPerPiece || '',
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(ingRows), 'Ingredients');

    const matRows = (starterMaterials || []).map(m => ({
      Name: m.name, Category: catName(starterMaterialCategories, m.categoryId), Quantity: m.quantity || '', Unit: m.unit || '',
      'Purchase Amount': m.purchaseAmount || '', 'Price Per Unit': m.pricePerUnit || '', Notes: m.notes || '',
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(matRows), 'Materials');

    const eqRows = (starterEquipment || []).map(e => ({
      Name: e.name, Category: catName(starterEquipmentCategories, e.categoryId),
      'Purchase Amount': e.purchaseAmount || '', Notes: e.notes || '',
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(eqRows), 'Equipment');

    const catSheets = [
      { cats: starterIngredientCategories, name: 'Ingredient Categories' },
      { cats: starterMaterialCategories, name: 'Material Categories' },
      { cats: starterEquipmentCategories, name: 'Equipment Categories' },
      { cats: starterRecipeCategories, name: 'Recipe Categories' },
    ];
    catSheets.forEach(({ cats, name }) => {
      const rows = (cats || []).map(c => ({ Name: c.name, Notes: c.notes || '' }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), name);
    });

    XLSX.writeFile(wb, `starter-pack-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div className="space-y-6">
      {/* Desktop: Full header */}
      <div className="hidden md:flex md:items-start md:justify-between">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold tracking-tight flex items-center gap-2" data-testid="heading-starter-pack">
            <Gift className="h-8 w-8" />
            Bento Starter Pack
          </h1>
          <p className="text-muted-foreground" data-testid="text-starter-pack-description">
            Manage template ingredients and materials for new users. These items will be available for all users to import into their pantry.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={downloadStarterPackCSV}>
            <Download className="h-4 w-4 mr-2" />
            Download All
          </Button>
          <Button
            variant="default"
            onClick={async () => {
              try {
                await apiRequest('POST', '/api/admin/generate-starter-images');
                showSuccess('Image generation started in background. This will take ~15 minutes.');
              } catch (error: any) {
                showError(error.message || 'Failed to start image generation');
              }
            }}
          >
            <ChefHat className="h-4 w-4 mr-2" />
            Generate Images
          </Button>
        </div>
      </div>

      {/* Mobile: Compact header */}
      <div className="md:hidden flex items-center justify-between">
        <h1 className="text-xl font-bold flex items-center gap-2" data-testid="heading-starter-pack">
          <Gift className="h-6 w-6" />
          Bento Starter Pack
        </h1>
        <Button variant="outline" size="sm" onClick={downloadStarterPackCSV}>
          <Download className="h-4 w-4" />
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {/* Desktop: Horizontal tabs */}
        <TabsList className="hidden md:inline-flex">
          <TabsTrigger value="ingredients" data-testid="tab-ingredients">
            <Package className="h-4 w-4 mr-2" />
            Ingredients ({starterIngredients?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="materials" data-testid="tab-materials">
            <Wrench className="h-4 w-4 mr-2" />
            Materials ({starterMaterials?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="equipment" data-testid="tab-equipment">
            <Hammer className="h-4 w-4 mr-2" />
            Equipment
          </TabsTrigger>
          {isAdminWorkspace && (
            <TabsTrigger value="categories" data-testid="tab-categories">
              <Tags className="h-4 w-4 mr-2" />
              Categories
            </TabsTrigger>
          )}
        </TabsList>

        {/* Mobile: Icon grid */}
        <div className="grid grid-cols-4 gap-2 md:hidden">
          <button
            onClick={() => setActiveTab("ingredients")}
            className={`flex flex-col items-center gap-1 p-3 rounded-lg border transition-colors ${
              activeTab === "ingredients"
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background hover:bg-accent"
            }`}
            data-testid="tab-ingredients"
          >
            <Package className="h-5 w-5" />
            <span className="text-xs font-medium">Ingredients</span>
          </button>
          <button
            onClick={() => setActiveTab("materials")}
            className={`flex flex-col items-center gap-1 p-3 rounded-lg border transition-colors ${
              activeTab === "materials"
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background hover:bg-accent"
            }`}
            data-testid="tab-materials"
          >
            <Wrench className="h-5 w-5" />
            <span className="text-xs font-medium">Materials</span>
          </button>
          <button
            onClick={() => setActiveTab("equipment")}
            className={`flex flex-col items-center gap-1 p-3 rounded-lg border transition-colors ${
              activeTab === "equipment"
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background hover:bg-accent"
            }`}
            data-testid="tab-equipment"
          >
            <Hammer className="h-5 w-5" />
            <span className="text-xs font-medium">Equipment</span>
          </button>
          {isAdminWorkspace && (
            <button
              onClick={() => setActiveTab("categories")}
              className={`flex flex-col items-center gap-1 p-3 rounded-lg border transition-colors ${
                activeTab === "categories"
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background hover:bg-accent"
              }`}
              data-testid="tab-categories"
            >
              <Tags className="h-5 w-5" />
              <span className="text-xs font-medium">Categories</span>
            </button>
          )}
        </div>

        <TabsContent value="ingredients" className="space-y-4">
          <StarterIngredientsTab
            starterIngredients={starterIngredients}
            ingredientsLoading={ingredientsLoading}
            starterIngredientCategories={starterIngredientCategories}
            currency={currency}
            isAdmin={isAdminWorkspace}
            ingredientPage={ingredientPage}
            setIngredientPage={setIngredientPage}
            ingredientSortField={ingredientSortField}
            setIngredientSortField={setIngredientSortField}
            ingredientSortDirection={ingredientSortDirection}
            setIngredientSortDirection={setIngredientSortDirection}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            onEdit={openEditIngredient}
            onDelete={(id) => deleteIngredientMutation.mutate({ id })}
            onImport={(id) => importIngredientMutation.mutate(id)}
            onImportAll={() => importAllIngredientsMutation.mutate()}
            onUpload={() => setUploadDialogOpen(true)}
            onAdd={() => setLocation('/starter-pack/ingredient')}
            onView={(item) => {
              setLocation(`/starter-pack/ingredient/view?id=${item._id || item.id}`);
            }}
            importIngredientMutation={importIngredientMutation}
            importAllIngredientsMutation={importAllIngredientsMutation}
            deleteIngredientMutation={deleteIngredientMutation}
          />
        </TabsContent>

        <TabsContent value="materials" className="space-y-4">
          <StarterMaterialsTab
            starterMaterials={starterMaterials}
            materialsLoading={materialsLoading}
            starterMaterialCategories={starterMaterialCategories}
            currency={currency}
            isAdmin={isAdminWorkspace}
            materialPage={materialPage}
            setMaterialPage={setMaterialPage}
            materialSortField={materialSortField}
            setMaterialSortField={setMaterialSortField}
            materialSortDirection={materialSortDirection}
            setMaterialSortDirection={setMaterialSortDirection}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            importAllMaterialsMutation={importAllMaterialsMutation}
            deleteMaterialMutation={deleteMaterialMutation}
            materialSearchQuery={materialSearchQuery}
            setMaterialSearchQuery={setMaterialSearchQuery}
            onAdd={() => setLocation('/starter-pack/material')}
            onEdit={(item) => setLocation(`/starter-pack/material?edit=${item._id}`)}
            onDelete={(id) => deleteMaterialMutation.mutate({ id })}
            onUpload={() => setUploadMaterialDialogOpen(true)}
            onView={(item) => setLocation(`/starter-pack/material/view?id=${item._id || item.id}`)}
          />
        </TabsContent>

        <TabsContent value="equipment" className="space-y-4">
          <StarterEquipmentTab
            starterEquipment={starterEquipment}
            equipmentLoading={equipmentLoading}
            starterEquipmentCategories={starterEquipmentCategories}
            currency={currency}
            isAdmin={isAdminWorkspace}
            equipmentPage={equipmentPage}
            setEquipmentPage={setEquipmentPage}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            equipmentSearchQuery={equipmentSearchQuery}
            setEquipmentSearchQuery={setEquipmentSearchQuery}
            onAdd={() => setLocation('/starter-pack/equipment')}
            onEdit={(item) => setLocation(`/starter-pack/equipment?edit=${item._id}`)}
            onDelete={(id) => deleteEquipmentMutation.mutate({ id })}
            onUpload={() => setEquipmentUploadDialogOpen(true)}
            onView={(item) => setLocation(`/starter-pack/equipment/view?id=${item._id || item.id}`)}
          />
        </TabsContent>

        {/* Equipment Upload placeholder */}
        {equipmentUploadDialogOpen && (
          <Dialog open={equipmentUploadDialogOpen} onOpenChange={setEquipmentUploadDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Equipment CSV Upload</DialogTitle>
                <DialogDescription>
                  Equipment CSV upload is not yet implemented. Please add equipment manually.
                </DialogDescription>
              </DialogHeader>
            </DialogContent>
          </Dialog>
        )}

        {isAdminWorkspace && (
          <TabsContent value="categories" className="space-y-4">
            <StarterCategoriesTab
              starterIngredientCategories={starterIngredientCategories}
              starterMaterialCategories={starterMaterialCategories}
              starterEquipmentCategories={starterEquipmentCategories}
              starterRecipeCategories={starterRecipeCategories}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              createIngredientCategoryMutation={createIngredientCategoryMutation}
              updateIngredientCategoryMutation={updateIngredientCategoryMutation}
              deleteIngredientCategoryMutation={deleteIngredientCategoryMutation}
              createMaterialCategoryMutation={createMaterialCategoryMutation}
              updateMaterialCategoryMutation={updateMaterialCategoryMutation}
              deleteMaterialCategoryMutation={deleteMaterialCategoryMutation}
              createEquipmentCategoryMutation={createEquipmentCategoryMutation}
              updateEquipmentCategoryMutation={updateEquipmentCategoryMutation}
              deleteEquipmentCategoryMutation={deleteEquipmentCategoryMutation}
              createRecipeCategoryMutation={createRecipeCategoryMutation}
              updateRecipeCategoryMutation={updateRecipeCategoryMutation}
              deleteRecipeCategoryMutation={deleteRecipeCategoryMutation}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
});

export default function StarterPack() {
  return <StarterPackContent />;
}
